import React, {
  cloneElement,
  isValidElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Accordion,
  AccordionGroup,
  AccordionContent,
  AccordionGroupContext,
  CustomAccordionHeader,
} from 'core/controls/accordion/Accordion';
import Radio from 'core/controls/radio/Radio';
import { SvgIcon } from 'core/icons';
import { combineClassNames } from '../../../core/css';
import { useTranslate, Translate } from 'core/translate';
import Checkbox from '../../../core/controls/checkbox/Checkbox';
import useAnimateScroll from '../../hooks/useAnimateScroll';
import useLargeBreakpoint from '../../hooks/useLargeBreakpoint';

const FeatureFilterAccordionInner = ({
  pillsList = [],
  children = null,
  analyticsKey = null,
  translateModText = '',
  dialog = null,
  useMobileDialog = false,
  responsivePills = false,
}) => {
  const translate = useTranslate();
  const { isOpen, onToggle } = useContext(AccordionGroupContext);
  const [waitedForFirstRender, setWaitedForFirstRender] = useState(false);

  const getAriaLabel = (isOpen, selection) =>
    translate('feature.filter.control.aria.v3', {
      filterState: isOpen ? 'Close' : 'Open',
      selection: selection,
    });

  const getContent = () => {
    if (isOpen) {
      if (!useMobileDialog || !waitedForFirstRender) {
        return children;
      }
      // TODO: Unmount this modal gracefully (for animation when transitioning from lg bp -> small)
      return (
        dialog && isValidElement(dialog) && cloneElement(dialog, { onToggle })
      );
    }
  };

  useEffect(() => {
    // TODO: Replace with static query? We set initial isAtLeastLarge = false
    // Because of this, we mount / unmount modal on init and it bugs the overlay.
    setWaitedForFirstRender(true);
  }, []);

  return (
    <>
      <CustomAccordionHeader className="feature-filter__header">
        <button
          className={combineClassNames(
            'feature-filter__expansion-control',
            'stat-expand-icon'
          )}
          onClick={onToggle}
          data-dtm={analyticsKey}
          aria-label={getAriaLabel(
            isOpen,
            translateModText.replace(/[\])}[{(]/g, '')
          )}>
          {' '}
          <SvgIcon name="filter" className="feature-filter__icon" />
          <span className="feature-filter__expansion-text">
            <Translate value="feature.filter.label.v3" />
            <span className="feature-filter__expansion-text-modifier">
              {translateModText ? ' ' + translateModText : ''}
            </span>
          </span>
          <SvgIcon
            name="chevron-up"
            className={`feature-filter__icon ${isOpen ? '' : 'hidden'}`}
          />
          <SvgIcon
            name="chevron-down"
            className={`feature-filter__icon ${isOpen ? 'hidden' : ''}`}
          />
        </button>{' '}
        {responsivePills ? (
          <FeatureFilterPillsCarousel
            isOpen
            onClick={onToggle}
            pillsList={pillsList}
            ariaLabel={{ getAriaLabel, isOpen }}
            analyticsKey={analyticsKey}
          />
        ) : (
          <FeatureFilterPills
            isOpen
            onClick={onToggle}
            pillsList={pillsList}
            ariaLabel={{ getAriaLabel, isOpen }}
            analyticsKey={analyticsKey}
          />
        )}
      </CustomAccordionHeader>
      <AccordionContent className="feature-filter__content">
        {getContent(onToggle, isOpen)}
      </AccordionContent>
    </>
  );
};

// TODO: Custom radio similiar to CustomCheckbox we used for option sort. (but CustomRadioButton)
export const FeatureFilterRadio = (props) => {
  const {
    primaryDescription,
    secondaryDescription,
    infoIcon = null,
    disabled = false,
    name,
    ...rest
  } = props;

  return (
    <Radio {...rest} customModifier="feature-filter" disabled={disabled}>
      <div className="feature-filter__option-description-container">
        <span
          className={combineClassNames(
            'feature-filter__option-description',
            name ? `feature-filter__option-description--${name}` : '',
            disabled ? 'feature-filter__option-description--disabled' : ''
          )}
          dangerouslySetInnerHTML={{ __html: primaryDescription }}
        />
        {infoIcon}
        {secondaryDescription && (
          <span
            className={combineClassNames(
              'feature-filter__option-secondary-description',
              disabled
                ? 'feature-filter__option-secondary-description--disabled'
                : ''
            )}
            dangerouslySetInnerHTML={{ __html: secondaryDescription }}></span>
        )}
      </div>
    </Radio>
  );
};

export const FeatureFilterCheckbox = (props) => {
  const {
    primaryDescription,
    secondaryDescription,
    infoIcon = null,
    disabled = false,
    ...rest
  } = props;

  return (
    <Checkbox
      {...rest}
      disabled={disabled}
      customModifier="feature-filter"
      customCheckName="check-mark">
      <div className="feature-filter__option-description-container">
        <span
          className={combineClassNames(
            'feature-filter__option-description',
            disabled ? 'feature-filter__option-description--disabled' : ''
          )}
          dangerouslySetInnerHTML={{ __html: primaryDescription }}
        />
        {infoIcon}
        {secondaryDescription && (
          <span
            className={combineClassNames(
              'feature-filter__option-secondary-description',
              disabled
                ? 'feature-filter__option-secondary-description--disabled'
                : ''
            )}
            dangerouslySetInnerHTML={{ __html: secondaryDescription }}
          />
        )}
      </div>
    </Checkbox>
  );
};

export const FeatureFilterPill = ({
  className,
  text,
  onClick,
  ariaLabel,
  hidden = false,
  analyticsKey,
}) => {
  return (
    <button
      className={`feature-filter__pill${
        hidden ? ' feature-filter__pill--hidden' : ''
      } stat-button-link`}
      tabIndex={hidden ? -1 : 0}
      onClick={onClick}
      aria-label={ariaLabel}
      dangerouslySetInnerHTML={{ __html: text }}
      data-dtm={analyticsKey}
    />
  );
};

export const FeatureFilterPillsCarousel = ({
  onClick,
  pillsList,
  ariaLabel,
  analyticsKey,
}) => {
  const [hasNext, setNext] = useState(false);
  const [hasPrev, setPrev] = useState(false);
  const [arrowLeft, setArrowLeft] = useState(0);
  const pillListRef = useRef();
  const { startElementAnimation } = useAnimateScroll();
  const isAtLeastLarge = useLargeBreakpoint();
  const { getAriaLabel, isOpen } = ariaLabel;

  const updateArrowLeft = () => {
    const expansionSection = document.querySelector(
      '.feature-filter__expansion-control'
    );

    setArrowLeft(expansionSection.offsetWidth);
  };

  const resizeCallback = () => {
    updatePosition();
    updateArrowLeft();
  };

  useEffect(() => {
    resizeCallback();
  }, [pillsList]);

  useEffect(() => {
    if (pillListRef && pillListRef.current) {
      window.addEventListener('resize', resizeCallback);

      return () => {
        window.removeEventListener('resize', resizeCallback);
      };
    }
  }, [pillListRef]);

  const updatePosition = (scrollDir = null) => {
    const pillListEl = pillListRef.current;
    const fullWidth = pillListEl.offsetWidth;
    const halfVisibleWidth = pillListEl.offsetWidth / 2;
    const scrollWidth = pillListEl.scrollWidth;

    const leftScreenOffset = pillListEl.scrollLeft;
    const availableScroll = scrollWidth - fullWidth - leftScreenOffset;

    let scrollDistance = 0;
    switch (scrollDir) {
      case 'right':
        scrollDistance =
          halfVisibleWidth <= availableScroll
            ? halfVisibleWidth
            : availableScroll;
        setNext(scrollDistance !== availableScroll);
        setPrev(scrollDistance === availableScroll);

        startElementAnimation(pillListEl, scrollDistance);
        break;
      case 'left':
        scrollDistance =
          leftScreenOffset - halfVisibleWidth >= 0
            ? -1 * halfVisibleWidth
            : -1 * leftScreenOffset;
        setNext(leftScreenOffset < scrollWidth);
        setPrev(leftScreenOffset + scrollDistance > 0);

        startElementAnimation(pillListEl, scrollDistance);
        break;
      default:
        setNext(Math.floor(availableScroll) > 0);
        setPrev(leftScreenOffset > 0);
        break;
    }
  };

  //Drag scroll functionality
  let pos = { left: 0, x: 0, moveX: null };

  const mouseDownHandler = function (e) {
    pos = {
      ...pos,
      left: pillListRef.current.scrollLeft,
      x: e.clientX,
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };

  const mouseUpHandler = function (e) {
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);

    pos = { ...pos, moveX: e.clientX };
    updatePosition();
  };

  const mouseMoveHandler = function (e) {
    const dx = e.clientX - pos.x;

    // Scroll the element
    pillListRef.current.scrollLeft = pos.left - dx;
  };

  return (
    <div
      className="feature-filter__responsive-pills-list"
      ref={pillListRef}
      onMouseDown={mouseDownHandler}
      onScroll={() => updatePosition()}>
      {pillsList.length > 0 ? (
        pillsList.map((pillText) => (
          <FeatureFilterPill
            key={pillText}
            text={pillText}
            onClick={() => {
              if (pos.x === pos.moveX) {
                onClick();
              }
            }}
            ariaLabel={getAriaLabel(isOpen, pillText)}
            analyticsKey={analyticsKey}
          />
        ))
      ) : (
        <FeatureFilterPill
          hidden={true}
          text={'hidden'}
          onClick={() => {}}
          ariaLabel={''}
        />
      )}

      <div
        className={`feature-filter-toggle__button-container feature-filter-toggle__button-container--left stat-arrow-prev ${
          hasPrev
            ? 'feature-filter-toggle__button-container--gradient-left'
            : ''
        }`}
        style={{ left: arrowLeft }}>
        {isAtLeastLarge && hasPrev && (
          <button
            className="feature-filter-toggle__button"
            onClick={() => updatePosition('left')}>
            <SvgIcon
              name="trim-filter-carousel-left"
              className="feature-filter-carousel-toggles__chevron"
            />
          </button>
        )}
      </div>

      <div
        className={`feature-filter-toggle__button-container feature-filter-toggle__button-container--right stat-arrow-next ${
          hasNext
            ? 'feature-filter-toggle__button-container--gradient-right'
            : ''
        }`}>
        {isAtLeastLarge && hasNext && (
          <button
            className="feature-filter-toggle__button"
            onClick={() => updatePosition('right')}>
            <SvgIcon
              name="trim-filter-carousel-right"
              className="feature-filter-carousel-toggles__chevron"
            />
          </button>
        )}
      </div>
    </div>
  );
};

export const FeatureFilterPills = ({
  onClick,
  pillsList,
  ariaLabel,
  analyticsKey,
}) => {
  const { getAriaLabel, isOpen } = ariaLabel;

  return (
    <div className="feature-filter__pills-list">
      {pillsList.length > 0 ? (
        pillsList.map((pillText) => (
          <FeatureFilterPill
            key={pillText}
            text={pillText}
            onClick={onClick}
            ariaLabel={getAriaLabel(isOpen, pillText)}
            analyticsKey={analyticsKey}
          />
        ))
      ) : (
        <FeatureFilterPill
          hidden={true}
          text={'hidden'}
          onClick={() => {}}
          ariaLabel={''}
        />
      )}
    </div>
  );
};

export const FeatureFilterSection = ({
  onChange,
  children,
  title,
  link = null,
  disclosure = null,
  modifier = null,
}) => {
  return (
    <div className="feature-filter__section" onChange={onChange}>
      <span className="feature-filter__section-title">
        {title}
        {link}
      </span>
      {disclosure ? (
        <span className="feature-filter__disclosure">{disclosure}</span>
      ) : null}
      <div
        className={combineClassNames(
          'feature-filter__filter-list',
          modifier ? `feature-filter__filter-list--${modifier}` : ''
        )}>
        {children}
      </div>
    </div>
  );
};

export const FilterCountStatus = ({
  filterCountText,
  displayReset,
  handleReset,
  analyticsKey,
}) => {
  return (
    <div role="status" aria-live="assertive" aria-atomic="true">
      <div className="pop-con-filter__count-container">
        {filterCountText}
        {displayReset && (
          <a
            className={combineClassNames(
              'pop-con-filter__reset-btn',
              'stat-text-link'
            )}
            href="#"
            onClick={handleReset}
            data-dtm={analyticsKey}>
            <Translate value="common.reset.v3" />
          </a>
        )}
      </div>
    </div>
  );
};

export const FilterCount = ({
  filterCountText,
  isUpdating,
  displayReset,
  handleReset,
  analyticsKey,
}) => {
  return (
    <div
      className={`pop-con-filter-count pop-con-filter-count--desktop${
        isUpdating ? ' pop-con-filter-count--updating' : ''
      }`}>
      <FilterCountStatus
        filterCountText={filterCountText}
        displayReset={displayReset}
        handleReset={handleReset}
        analyticsKey={analyticsKey}
      />
    </div>
  );
};

const FeatureFilter = (props) => {
  const { openDefault, onToggle, isUpdating } = props;
  return (
    <Accordion isUpdating={isUpdating}>
      <AccordionGroup
        id="feature-filter"
        className="feature-filter"
        isOpenDefault={openDefault}
        onToggle={onToggle}>
        <FeatureFilterAccordionInner {...props} />
      </AccordionGroup>
    </Accordion>
  );
};

export default FeatureFilter;
